import type { Layout } from '@mentimeter/legacy-series-types/common';
import type { Slide as VotingSlide } from '../../../compatibility-overrides/voting-types';
import { getOrSetCache, type WithCache } from '../../../utils/get-or-set-cache';
import type { CompatibilitySlide } from '../../../compatibility-types';
import { layoutToQuestionLayout } from './layout-to-question-layout';

export const getQuestionLayout = (slide: CompatibilitySlide | VotingSlide) => {
  const { staticContent } = slide;

  // TODO: static content should not be undefined here based on types. Leaving this check for now to avoid unintended consequences.
  if (!staticContent) {
    return undefined;
  }

  // leaderboard and pin-on-image are a special case, since it does not have a layout in the new model, but
  // it has textSizeOffset. The old model has whole layout object for leaderboard and pin-on-image, but all the properties are static
  // there except `font_size_offset`.
  // To make it backwards compatible, we set the same defaults for layout as in the old model.
  if (
    staticContent.type === 'leaderboard' ||
    staticContent.type === 'pin-on-image'
  ) {
    const layout: Layout = {
      type: 'default',
      font_size_offset: staticContent.textSizeOffset,
      alignment_x: 1,
      alignment_y: 1,
      size: '33%',
    };

    const cacheKey = JSON.stringify(layout);

    return getOrSetCache(
      slide as WithCache<CompatibilitySlide>,
      'layout',
      cacheKey,
      layout,
    );
  }

  // image and document do not have layout in the old model
  if (staticContent.type === 'image' || staticContent.type === 'document') {
    return null;
  }

  if (!('layout' in staticContent)) {
    return undefined;
  }

  const textAlignment =
    'textAlignment' in staticContent ? staticContent.textAlignment : undefined;

  const fontSizeOffset =
    'textSizeOffset' in staticContent ? staticContent.textSizeOffset : 0;

  const questionLayout = layoutToQuestionLayout({
    layout: staticContent.layout,
    layoutPosition: staticContent.image?.imageLayoutPosition,
    inset: Boolean(staticContent.image?.imageInset),
    textAlignment,
    fontSizeOffset,
  });

  const cacheKey = JSON.stringify(questionLayout);

  return getOrSetCache(
    slide as WithCache<CompatibilitySlide>,
    'layout',
    cacheKey,
    questionLayout,
  );
};
